import { useEffect } from 'react';
import { View, Text, Platform } from 'react-native';
import Head from 'expo-router/head';
import { Target, Shield, Zap, Globe } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';

export default function AboutScreen() {
  useTrackScreen('About');

  // Organization JSON-LD structured data
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'about-org-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Inverio',
      url: 'https://inverio.ai',
      logo: 'https://inverio.ai/apple-touch-icon.png',
      description: 'AI-powered investment readiness scoring platform for startups and investors',
      email: 'support@inverio.ai',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CH',
      },
      foundingDate: '2026',
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('about-org-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  // HowTo JSON-LD structured data
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'about-howto-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Get Your Investment Readiness Score on Inverio',
      description: 'Get scored, matched, and funded in 4 simple steps',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Create Your Profile',
          text: 'Sign up as a startup founder or investor. Complete your profile with company details, investment thesis, or portfolio preferences.',
        },
        {
          '@type': 'HowToStep',
          name: 'Upload Your Documents',
          text: 'Add pitch decks, financials, and team bios to your data room. Our AI analyzes them to build a comprehensive picture.',
        },
        {
          '@type': 'HowToStep',
          name: 'Get Your Score',
          text: 'Receive an Investment Readiness Score across 15+ criteria covering team, traction, market, and financials.',
        },
        {
          '@type': 'HowToStep',
          name: 'Connect with Investors',
          text: 'Get matched with investors whose thesis aligns with your startup. Investors browse pre-scored, vetted opportunities.',
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('about-howto-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <PublicLayout title="About Inverio">
      {Platform.OS === 'web' && (
        <Head>
          <title>About Inverio — AI Investment Readiness Platform</title>
          <meta name="description" content="Learn how Inverio helps startups become investor-ready through AI scoring, document analysis, and investor matching. Swiss-built platform." />
          <link rel="canonical" href="https://inverio.ai/about" />
          <meta property="og:title" content="About Inverio — AI Investment Readiness Platform" />
          <meta property="og:description" content="Learn how Inverio helps startups become investor-ready through AI scoring, document analysis, and investor matching. Swiss-built platform." />
          <meta property="og:url" content="https://inverio.ai/about" />
        </Head>
      )}
      {/* Mission */}
      <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-8">
        <Text style={{ color: '#FFFFFF' }} className="text-lg font-bold mb-3">
          Our Mission
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed mb-3">
          Inverio helps startups become investor-ready through AI-powered scoring, document analysis, and investor matching — while giving investors a trusted pipeline of pre-vetted, scored opportunities.
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed">
          We believe fundraising should be transparent, data-driven, and accessible. Our platform standardizes how startups are evaluated, giving founders actionable insights to improve their investment readiness and connecting them with the right investors.
        </Text>
      </Animated.View>

      {/* Values */}
      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-8">
        <Text style={{ color: '#FFFFFF' }} className="text-lg font-bold mb-4">
          What We Stand For
        </Text>
        <View className="gap-4">
          <ValueCard
            icon={<Target size={22} color={'#4fd1c5'} />}
            title="Transparency"
            description="Every score comes with a detailed breakdown. No black boxes — founders see exactly where they stand and what to improve."
          />
          <ValueCard
            icon={<Shield size={22} color={'#4fd1c5'} />}
            title="Data Privacy"
            description="Your data is yours. We process documents to deliver the service, but never sell your information or train AI models on your content."
          />
          <ValueCard
            icon={<Zap size={22} color={'#4fd1c5'} />}
            title="AI-Powered, Human-Centered"
            description="We use AI to accelerate analysis, not replace judgment. Our scores are a starting point for due diligence, not a substitute for it."
          />
          <ValueCard
            icon={<Globe size={22} color={'#4fd1c5'} />}
            title="Swiss Quality"
            description="Built in Switzerland with Swiss data protection standards. We operate under the Swiss Federal Act on Data Protection (FADP)."
          />
        </View>
      </Animated.View>

      {/* How It Works */}
      <Animated.View entering={FadeInDown.delay(250).duration(500)} className="mb-8">
        <Text style={{ color: '#FFFFFF' }} className="text-lg font-bold mb-3">
          How It Works
        </Text>
        <View className="gap-3">
          <StepItem number="1" title="Create Your Profile" description="Sign up as a startup founder or investor. Complete your profile with company details, investment thesis, or portfolio preferences." />
          <StepItem number="2" title="Upload Your Documents" description="Add pitch decks, financials, and team bios to your data room. Our AI analyzes them to build a comprehensive picture." />
          <StepItem number="3" title="Get Your Score" description="Receive an Investment Readiness Score across 15+ criteria covering team, traction, market, and financials." />
          <StepItem number="4" title="Connect with Investors" description="Get matched with investors whose thesis aligns with your startup. Investors browse pre-scored, vetted opportunities." />
        </View>
      </Animated.View>

      {/* Company Info */}
      <Animated.View entering={FadeInDown.delay(300).duration(500)}>
        <View
          className="p-5 rounded-2xl"
          style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.08)',
          }}
        >
          <Text style={{ color: '#FFFFFF' }} className="font-bold mb-2">
            Inverio
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed mb-1">
            A service of A. Kouadio INVARK · Geneva, Switzerland
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed">
            Email: support@inverio.ai
          </Text>
        </View>
      </Animated.View>
    </PublicLayout>
  );
}

function ValueCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <View
      className="flex-row gap-4 p-4 rounded-2xl"
      style={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
      }}
    >
      <View
        className="w-11 h-11 rounded-xl items-center justify-center"
        style={{ backgroundColor: 'rgba(79,209,197,0.15)' }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <Text style={{ color: '#FFFFFF' }} className="font-semibold mb-1">
          {title}
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed">
          {description}
        </Text>
      </View>
    </View>
  );
}

function StepItem({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <View className="flex-row gap-4 items-start">
      <View
        className="w-8 h-8 rounded-full items-center justify-center"
        style={{ backgroundColor: '#4fd1c5' }}
      >
        <Text className="text-white font-bold text-sm">{number}</Text>
      </View>
      <View className="flex-1">
        <Text style={{ color: '#FFFFFF' }} className="font-semibold mb-0.5">
          {title}
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)' }} className="text-sm leading-relaxed">
          {description}
        </Text>
      </View>
    </View>
  );
}
